import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'

interface ErrorScreenProps {
    error?: unknown
    /** Re-render the failed part (router reset); falls back to a page reload. */
    reset?: () => void
}

const detailsOf = (error: unknown): string => {
    if (error instanceof Error) return error.message || error.name
    if (typeof error === 'string') return error
    return ''
}

/**
 * What people see when a page fails to render. Plain language, a way forward,
 * and the technical message tucked away for whoever helps them.
 */
export function ErrorScreen({ error, reset }: ErrorScreenProps) {
    const details = detailsOf(error)
    return (
        <div role="alert" className="mx-auto flex min-h-[60vh] w-full max-w-lg flex-col justify-center gap-6 px-4 py-16">
            <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-semibold">We couldn’t show this page</h1>
                <p className="text-muted-foreground">
                    Something went wrong on our side. This is usually temporary. Your drafts are saved automatically, so
                    you won’t lose your progress.
                </p>
            </div>
            <div className="flex flex-wrap gap-2">
                <Button onClick={() => (reset ? reset() : window.location.reload())}>Try again</Button>
                <Button variant="outline" asChild>
                    <a href="/">Go to the start page</a>
                </Button>
            </div>
            <p className="text-sm text-muted-foreground">If this keeps happening, please let your administrator know.</p>
            {details && (
                <details className="text-sm text-muted-foreground">
                    <summary className="cursor-pointer select-none">Technical details for support</summary>
                    <pre className="mt-2 overflow-x-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap">{details}</pre>
                </details>
            )}
        </div>
    )
}

export function NotFoundScreen() {
    return (
        <div className="mx-auto flex min-h-[60vh] w-full max-w-lg flex-col justify-center gap-6 px-4 py-16">
            <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-semibold">We couldn’t find that page</h1>
                <p className="text-muted-foreground">
                    The link may be out of date, or you may not have access to it.
                </p>
            </div>
            <div>
                <Button asChild>
                    <a href="/">Go to the start page</a>
                </Button>
            </div>
        </div>
    )
}

interface BoundaryState {
    error: unknown
    failed: boolean
}

/** Last-resort boundary for crashes outside the router (sign-in, theme, providers). */
export class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
    state: BoundaryState = { error: null, failed: false }

    static getDerivedStateFromError(error: unknown): BoundaryState {
        return { error, failed: true }
    }

    componentDidCatch(error: unknown, info: ErrorInfo) {
        console.error('Unhandled UI error', error, info.componentStack)
    }

    render() {
        if (this.state.failed) {
            return <ErrorScreen error={this.state.error} reset={() => this.setState({ error: null, failed: false })} />
        }
        return this.props.children
    }
}
